import React from 'react'
import PropTypes from 'prop-types'
import { numberToCurrency } from '../../shared/helpers/String'

const planShape = PropTypes.shape({
  sku: PropTypes.string,
  name: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
})

// One radio per plan on sale, keyed by SKU. This replaces the monthly/annual
// pair once the catalogue carries plans, because the shopper is no longer
// choosing a billing interval -- the plans are all annual and differ by what
// they include. The radio's value IS the SKU the cart endpoints read behind the
// flag, so the choice the shopper can see is the one that reaches the cart.
function PlanOptions({ membershipPlans, selectedOption, handleOptionChange }) {
  return membershipPlans.map((plan) => (
    <div className="form-check mb-2" key={plan.sku}>
      <label className="d-block w-100">
        <div className={`card border border-secondary border-2 rounded-3 ${selectedOption === plan.sku ? 'border-checked' : ''}`}>
          <div className="card-body d-flex">
            <input
              className="form-check-input border-secondary"
              type="radio"
              name="membershipOptions"
              value={plan.sku}
              checked={selectedOption === plan.sku}
              onChange={handleOptionChange}
            />
            <span className="ms-2">
              {plan.name}
              {' - '}
              {numberToCurrency(plan.price)}
              /year
            </span>
          </div>
        </div>
      </label>
    </div>
  ))
}

PlanOptions.propTypes = {
  membershipPlans: PropTypes.arrayOf(planShape).isRequired,
  selectedOption: PropTypes.string,
  handleOptionChange: PropTypes.func.isRequired,
}

PlanOptions.defaultProps = {
  selectedOption: null,
}

// The monthly-vs-annual Access pair, unchanged. Extracted only so the plan list
// above can stand beside it as a sibling rather than be nested into it.
function IntervalOptions({
  selectedOption,
  handleOptionChange,
  accessMembershipPrice,
  annualAccessMembershipPrice,
}) {
  return (
    <>
      <div className="form-check mb-2">
        <label className="d-block w-100">
          <div className={`card border border-secondary border-2 rounded-3 ${selectedOption === 'monthly' ? 'border-checked' : ''}`}>
            <div className="card-body d-flex">
              <input className="form-check-input border-secondary" type="radio" name="membershipOptions" value="monthly" onChange={handleOptionChange} />
              <span className="ms-2">
                Monthly Membership -
                {' '}
                {numberToCurrency(accessMembershipPrice)}
              </span>
            </div>
          </div>
        </label>
      </div>
      <div className="form-check mb-2">
        <label className="d-block w-100">
          <div className={`card border border-secondary border-2 rounded-3 ${selectedOption === 'yearly' ? 'border-checked' : ''}`}>
            <div className="card-body d-flex flex-column">
              <div className="d-sm-flex align-items-center">
                <div className="d-flex">
                  <input className="form-check-input border-secondary" type="radio" name="membershipOptions" value="yearly" onChange={handleOptionChange} />
                  <div className="ms-2">
                    <div>
                      Annual Membership -
                      {' '}
                      {numberToCurrency(annualAccessMembershipPrice)}
                    </div>
                    <div className="d-sm-none membership-savings">
                      Save over $
                      {Math.floor((accessMembershipPrice * 12) - annualAccessMembershipPrice)}
                      {' '}
                      for the year!
                    </div>
                  </div>
                </div>
                <span className="ms-sm-auto mt-2 mt-sm-0 d-none d-sm-block membership-savings">
                  Save over $
                  {Math.floor((accessMembershipPrice * 12) - annualAccessMembershipPrice)}
                  {' '}
                  for the year!
                </span>
              </div>
            </div>
          </div>
        </label>
      </div>
    </>
  )
}

IntervalOptions.propTypes = {
  selectedOption: PropTypes.string,
  handleOptionChange: PropTypes.func.isRequired,
  accessMembershipPrice: PropTypes.string.isRequired,
  annualAccessMembershipPrice: PropTypes.string.isRequired,
}

IntervalOptions.defaultProps = {
  selectedOption: null,
}

export default function PriceBlockMembershipOptions({
  selectedOption,
  handleOptionChange,
  accessMembershipPrice,
  annualAccessMembershipPrice,
  product,
  membershipPlans = [],
  membershipPlansEnabled = false,
}) {
  const hasPlans = membershipPlans.length > 0
  // Enabled with nothing on sale is its own state, not the legacy one. The
  // endpoints ignore `membership_option` whenever the flag is on, so falling
  // back to the interval radios here would put a control on screen whose
  // selection adds nothing at all -- the failure mode this picker exists to
  // remove. Say so instead.
  const plansUnavailable = membershipPlansEnabled && !hasPlans

  const heading = () => {
    if (plansUnavailable) return 'Memberships are not on sale right now'
    return hasPlans ? 'Select your membership' : 'Select your membership payment plan'
  }

  return (
    <>
      <div className="d-flex membership-options-heading flex-column flex-sm-row">
        <div className="order-2 order-sm-1 my-2 my-sm-0">
          <span>{heading()}</span>
        </div>
        <div className="ms-sm-auto order-1 order-sm-2">
          <div className="d-flex align-items-center">
            <span className="me-1">Already a member?</span>
            <a href={`/account/login/?redirect_url=${product.permalink}`} className="btn btn-link p-0">Login</a>
          </div>
        </div>
      </div>
      <div className="membership-options mt-1 mb-3">
        {plansUnavailable && (
          <p className="text-muted small mb-0 membership-options-unavailable">
            We cannot start a membership right now, so this order cannot be placed yet.
            Please check back soon.
          </p>
        )}
        {hasPlans && (
          <PlanOptions
            membershipPlans={membershipPlans}
            selectedOption={selectedOption}
            handleOptionChange={handleOptionChange}
          />
        )}
        {!membershipPlansEnabled && (
          <IntervalOptions
            selectedOption={selectedOption}
            handleOptionChange={handleOptionChange}
            accessMembershipPrice={accessMembershipPrice}
            annualAccessMembershipPrice={annualAccessMembershipPrice}
          />
        )}
      </div>
    </>
  )
}

PriceBlockMembershipOptions.propTypes = {
  selectedOption: PropTypes.string,
  handleOptionChange: PropTypes.func.isRequired,
  accessMembershipPrice: PropTypes.string.isRequired,
  annualAccessMembershipPrice: PropTypes.string.isRequired,
  product: PropTypes.shape({
    permalink: PropTypes.string,
  }).isRequired,
  membershipPlans: PropTypes.arrayOf(planShape),
  membershipPlansEnabled: PropTypes.bool,
}

PriceBlockMembershipOptions.defaultProps = {
  selectedOption: null,
  membershipPlans: [],
  membershipPlansEnabled: false,
}
